import { DetailForm } from '@/@diffrag/form/DetailForm'
import { FormFieldType } from '@/@diffrag/form/fields/type'
import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import {
  IAdminClinicInterviewROO1,
  IAdminClinicConfirmDoctorProfileRequestO1,
  IAdminClinicConfirmDoctorProfileRequestO1StatusEnum,
} from 'api'
import { useNavigate } from 'react-router-dom'
import parse from 'html-react-parser'

export default function DoctorInterviewEdit() {
  const navigate = useNavigate()
  const id = useQueryValue('id', '')

  const { data, refetch } = useApi({
    id: 'interviewDetail',
    fn: api().adminClinicInterview.adminClinicInterviewDetail,
    params: Number(id),
    enabled: !!id,
  })

  const onSave = async (data: IAdminClinicInterviewROO1) => {
    if (id) {
      const updateData: IAdminClinicConfirmDoctorProfileRequestO1 = {
        status:
          data.status as unknown as IAdminClinicConfirmDoctorProfileRequestO1StatusEnum,
        rejectReason: data.rejectReason || '',
      }

      await api().adminClinicInterview.approveClinicDoctorProfile(
        Number(id),
        updateData,
      )
    }

    refetch()
    navigate('/approval-management/doctor-interview', {
      replace: true,
    })
  }

  return (
    <ContentDetailView title="원장님 인터뷰 승인" data={data} onSave={onSave}>
      <DetailForm
        data={data}
        fields={[
          {
            label: '병원명',
            key: 'clinicName',
            type: FormFieldType.STRING,
            readOnly: true,
          },
          {
            label: '원장님성함',
            key: 'doctorName',
            type: FormFieldType.CUSTOM_READ_ONLY,
            render: (data) => {
              const names = [
                { lang: 'ko', name: data?.doctorName, label: '한국어' },
                { lang: 'zh-CN', name: data?.nameZhcn, label: '简体中文' },
                { lang: 'zh-TW', name: data?.nameZhtw, label: '繁體中文' },
                { lang: 'en', name: data?.nameEn, label: 'English' },
                { lang: 'ja', name: data?.nameJa, label: '日本語' },
                { lang: 'th', name: data?.nameTh, label: 'ไทย' },
                { lang: 'mn', name: data?.nameMn, label: 'Монгол' },
                { lang: 'ru', name: data?.nameRu, label: 'Русский' },
              ].filter(
                (item) => item.name || item.lang === 'mn' || item.lang === 'ru',
              )

              if (names.length === 0) return '-'
              if (names.length === 1) return names[0].name || '-'

              return (
                <div className="space-y-1">
                  {names.map((item) => (
                    <div key={item.lang} className="text-sm">
                      <span className="font-semibold text-blue-600">
                        [{item.label}]
                      </span>{' '}
                      {item.name || '-'}
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            label: '원장님 소개',
            key: 'content',
            type: FormFieldType.CUSTOM_READ_ONLY,
            render: (data) => {
              const contents = [
                { lang: 'ko', content: data?.content, label: '한국어' },
                {
                  lang: 'zh-CN',
                  content: data?.contentZhcn,
                  label: '简体中文',
                },
                {
                  lang: 'zh-TW',
                  content: data?.contentZhtw,
                  label: '繁體中文',
                },
                { lang: 'en', content: data?.contentEn, label: 'English' },
                { lang: 'ja', content: data?.contentJa, label: '日本語' },
                { lang: 'th', content: data?.contentTh, label: 'ไทย' },
                { lang: 'mn', content: data?.contentMn, label: 'Монгол' },
                { lang: 'ru', content: data?.contentRu, label: 'Русский' },
              ].filter(
                (item) =>
                  item.content || item.lang === 'mn' || item.lang === 'ru',
              )

              if (contents.length === 0) return '-'
              if (contents.length === 1) {
                return (
                  <div className="min-h-[150px] w-full rounded border bg-gray-50 p-3">
                    {contents[0].content ? parse(contents[0].content) : '-'}
                  </div>
                )
              }

              return (
                <div className="w-full space-y-4">
                  {contents.map((item) => (
                    <div key={item.lang} className="space-y-2">
                      <div className="border-b border-blue-200 pb-2 text-lg font-bold text-blue-600">
                        {item.label}
                      </div>
                      <div className="min-h-[150px] w-full rounded border bg-gray-50 p-3">
                        {item.content ? parse(item.content) : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            label: 'Q&A',
            key: 'qna',
            type: FormFieldType.CUSTOM_READ_ONLY,
            render: (data) => {
              if (!data?.qna || data.qna.length === 0) {
                return <span>Q&A가 없습니다.</span>
              }

              // 언어 코드를 실제 언어명으로 매핑
              const languageMap: Record<string, string> = {
                ko: '한국어',
                'zh-CN': '简体中文',
                'zh-TW': '繁體中文',
                en: 'English',
                ja: '日本語',
                th: 'ไทย',
                mn: 'Монгол',
                ru: 'Русский',
              }

              // language별로 그룹화
              const groupedByLanguage = data.qna.reduce(
                (acc, item) => {
                  const languageCode = item.language || 'ko'
                  const languageName = languageMap[languageCode] || languageCode
                  if (!acc[languageName]) {
                    acc[languageName] = []
                  }
                  acc[languageName].push(item)
                  return acc
                },
                {} as Record<string, typeof data.qna>,
              )

              return (
                <div className="space-y-4">
                  {Object.entries(groupedByLanguage).map(
                    ([language, items]) => (
                      <div key={language} className="space-y-2">
                        <div className="border-b border-blue-200 pb-2 text-lg font-bold text-blue-600">
                          {language}
                        </div>
                        <div className="space-y-2">
                          {items.map((item, index) => (
                            <div
                              key={index}
                              className="rounded border bg-gray-50 p-3"
                            >
                              <div className="text-sm font-semibold text-gray-700">
                                Q: {item.question}
                              </div>
                              <div className="mt-2 text-sm text-gray-600">
                                A: {item.answer}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )
            },
          },
          {
            label: '인터뷰영상',
            key: 'youtubeURL',
            type: FormFieldType.STRING,
            readOnly: true,
          },
          {
            label: '등록일',
            key: 'createDate',
            type: FormFieldType.DATE,
            readOnly: true,
          },
          {
            label: '상태',
            key: 'status',
            type: FormFieldType.SELECT,
            options: [
              { label: '대기', value: 'PENDING' },
              { label: '승인', value: 'APPROVED' },
              { label: '거절', value: 'REJECTED' },
            ],
            required: true,
          },
          {
            label: '승인일',
            key: 'approveDate',
            type: FormFieldType.DATE,
            readOnly: true,
          },
          {
            label: '거절일',
            key: 'rejectDate',
            type: FormFieldType.DATE,
            readOnly: true,
          },
          {
            label: '거절사유',
            key: 'rejectReason',
            type: FormFieldType.TEXT,
          },

          // {
          //   label: '수정 요청 내용',
          //   key: 'tempData',
          //   type: FormFieldType.CUSTOM_READ_ONLY,
          //   render: (data) => {
          //     console.log(data)
          //     if (!data?.tempData) {
          //       return <span>수정 요청 내용이 없습니다.</span>
          //     }

          //     // 언어 코드를 실제 언어명으로 매핑
          //     const languageMap: Record<string, string> = {
          //       ko: '한국어',
          //       'zh-CN': '简体中文',
          //       'zh-TW': '繁體中文',
          //       en: 'English',
          //       ja: '日本語',
          //       th: 'ไทย',
          //     }

          //     // language별로 그룹화
          //     const groupedByLanguage = data.tempData.request.reduce(
          //       (acc, item) => {
          //         const languageCode = item.language || 'ko'
          //         const languageName = languageMap[languageCode] || languageCode
          //         if (!acc[languageName]) {
          //           acc[languageName] = []
          //         }
          //         acc[languageName].push(item)
          //         return acc
          //       },
          //       {} as Record<string, typeof data.tempData.request>,
          //     )

          //     return (
          //       <div className="space-y-4">
          //         {Object.entries(groupedByLanguage).map(
          //           ([language, items]) => (
          //             <div key={language} className="space-y-2">
          //               <div className="border-b border-blue-200 pb-2 text-lg font-bold text-blue-600">
          //                 {language}
          //               </div>
          //               <div className="space-y-2">
          //                 {items.map((item, index) => (
          //                   <div
          //                     key={index}
          //                     className="rounded border bg-gray-50 p-3"
          //                   >
          //                     <div className="text-sm font-semibold text-gray-700">
          //                       Q: {item.question}
          //                     </div>
          //                     <div className="mt-2 text-sm text-gray-600">
          //                       A: {item.answer}
          //                     </div>
          //                   </div>
          //                 ))}
          //               </div>
          //             </div>
          //           ),
          //         )}
          //       </div>
          //     )
          //   },
          // },
        ]}
      />
    </ContentDetailView>
  )
}
